import { useMsal } from "@azure/msal-react";
import { useInfiniteQuery } from "@tanstack/react-query";
import type { MRT_ColumnFiltersState, MRT_SortingState } from "mantine-react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { postAPI } from "src/utils/fetch";
import { BACKEND_URL } from "./useMetadata";

export interface SearchAfterCursor {
    documentTimestamp: string;
    documentId: string;
}

export interface UseSearchAfterPaginationParams {
    queryKey: unknown[];
    index: string;
    sorting?: MRT_SortingState;
    columnFilters?: MRT_ColumnFiltersState;
    globalFilter?: string;
    // biome-ignore lint/suspicious/noExplicitAny: Payload structure varies by implementation
    buildPayload?: () => any[];
    enabled?: boolean;
    // biome-ignore lint/suspicious/noExplicitAny: Dependencies can be of any type
    resetDependencies?: any[];
    basePath?: string;
}

export const useSearchAfterPagination = ({
    queryKey,
    index,
    sorting = [],
    columnFilters = [],
    globalFilter,
    buildPayload,
    enabled = true,
    resetDependencies = [],
    basePath = "v1/search",
}: UseSearchAfterPaginationParams) => {
    const { instance } = useMsal();
    const [documentId, setDocumentId] = useState<string | null>(null);
    const [documentTimestamp, setDocumentTimestamp] = useState<string | null>(null);

    // Create stable references for dependency comparison
    const sortingKey = useMemo(() => JSON.stringify(sorting), [sorting]);
    const filtersKey = useMemo(() => JSON.stringify(columnFilters), [columnFilters]);

    // Reset pagination state when query parameters change
    // biome-ignore lint/correctness/useExhaustiveDependencies: We need to reset pagination when any query parameter changes
    useEffect(() => {
        setDocumentId(null);
        setDocumentTimestamp(null);
    }, [sortingKey, filtersKey, globalFilter, ...resetDependencies]);

    // Default payload builder for simple metadata filters
    const defaultBuildPayload = useCallback(() => {
        return (
            columnFilters
                ?.filter((val) =>
                    Array.isArray(val.value)
                        ? val.value.filter(Boolean).length > 0
                        : Boolean(val.value),
                )
                .map((filter) => {
                    const key = filter.id.split("__")[1] ?? filter.id;

                    let value = filter.value;
                    if (key === "timestamp" && Array.isArray(value)) {
                        value = value.map((item) => (item == null ? "" : item));
                    }

                    return {
                        key,
                        value,
                    };
                }) ?? []
        );
    }, [columnFilters]);

    const payloadBuilder = buildPayload || defaultBuildPayload;

    const performSearch = useCallback(
        async (usesPagination: boolean) => {
            if (globalFilter) {
                const url = new URL(`${basePath}/text`, BACKEND_URL);
                return await postAPI({
                    url: url.toString(),
                    instance,
                    payload: {
                        query: globalFilter,
                        metadata_filter: payloadBuilder(),
                    },
                });
            }

            const url = new URL(`${basePath}/${index}/metadata/docs`, BACKEND_URL);
            url.searchParams.set("max_result", "25");

            if (usesPagination && documentId && documentTimestamp) {
                url.searchParams.set("search_after_doc_id", documentId);
                url.searchParams.set("search_after_doc_timestamp", documentTimestamp);
            }

            return await postAPI({
                url: url.toString(),
                instance,
                payload: payloadBuilder(),
            });
        },
        [globalFilter, instance, index, payloadBuilder, documentId, documentTimestamp, basePath],
    );

    const queryResult = useInfiniteQuery({
        queryKey,
        queryFn: async ({ pageParam }) => {
            const cursor = pageParam as SearchAfterCursor | undefined;

            // For first page or when we have no cursor, don't use pagination
            const usesPagination = Boolean(cursor?.documentId && cursor.documentTimestamp);

            const response = await performSearch(usesPagination);
            const data = await response.json();

            const lastHit = data.hits?.hits?.at(-1);
            if (lastHit?.sort) {
                setDocumentTimestamp(lastHit.sort[0]);
                setDocumentId(lastHit.sort[1]);
            } else {
                setDocumentTimestamp(null);
                setDocumentId(null);
            }

            return data;
        },
        initialPageParam: undefined,
        getNextPageParam: (lastPage) => {
            const lastHit = lastPage?.hits?.hits?.at(-1);
            if (!lastHit?.sort) {
                return undefined;
            }

            const [nextTimestamp, nextId] = lastHit.sort;
            if (!nextTimestamp || !nextId) {
                return undefined;
            }

            return {
                documentTimestamp: nextTimestamp,
                documentId: nextId,
            };
        },
        enabled: enabled && (!!globalFilter || !!index),
        placeholderData: (previousData) => previousData,
        refetchOnWindowFocus: false,
    });

    return {
        ...queryResult,
        documentId,
        documentTimestamp,
    };
};
